// Central app-timezone override (device-local).
//
// "Global timezone" here means: one zone the whole app renders every instant in, overriding the
// machine's own zone. It is a local user preference (appTimeZone in user-preference-store) — "" (or
// unset) means "follow the system zone" and every date reads exactly as it did before this feature.
//
// Two mechanisms cover the whole app from this one place:
//   1. luxon — Settings.defaultZone is pointed at the override, so DateTime.local(), zone-less
//      parsing, and .toLocal() (which is literally setZone(Settings.defaultZone)) all re-express in
//      the chosen zone. That is the entire calendar surface in a single assignment.
//   2. native Intl — toLocaleString/-Time/-Date have no runtime "default zone" lever, so those
//      call sites pass withAppTimeZone(opts). When no override is set this passes the machine zone
//      explicitly, which is byte-for-byte identical to omitting it — so off-by-default is a no-op.
//
// The cached override + luxon zone are kept in sync with the store for the app's lifetime; the
// renderer imports this module first (see renderer.jsx) so the zone is applied before first paint.

import { Settings } from "luxon";

import {
  getUserPreferences,
  subscribeUserPreferences,
} from "@/lib/stores/user-preference-store";

// The machine's own zone (what the OS/browser reports). Resolved once — resolvedOptions() is not
// free and the host zone does not change during a session.
let systemTimeZone = "UTC";
try {
  systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
} catch {
  /* keep UTC */
}

// The override string ("" = follow the system zone). Cached so the hot path (formatting every
// message row, every calendar cell) is a plain variable read; refreshed on the pref-change event.
let overrideTimeZone = readOverrideFromPrefs();

function readOverrideFromPrefs() {
  try {
    const value = getUserPreferences()?.appTimeZone;
    return typeof value === "string" ? value.trim() : "";
  } catch {
    return "";
  }
}

/** The zone the app renders every instant in: the override when set, else the machine zone. */
export function appTimeZone() {
  return overrideTimeZone || systemTimeZone || "UTC";
}

/** True when a global override is in force (used to label the setting / cue the app-wide badge). */
export function isAppTimeZoneOverridden() {
  return Boolean(overrideTimeZone);
}

/** The unmodified machine zone — for the "Use system default (…)" hint in the picker. */
export function systemDefaultTimeZone() {
  return systemTimeZone || "UTC";
}

/**
 * Merge the app zone into Intl.DateTimeFormat options for a native toLocale* call.
 * `date.toLocaleTimeString(undefined, withAppTimeZone({ hour: "2-digit", minute: "2-digit" }))`.
 * Behaviour-preserving when no override is set (passes the host zone, same as omitting it).
 */
export function withAppTimeZone(options = {}) {
  return { ...options, timeZone: appTimeZone() };
}

// --- zone-aware calendar-day helpers ---------------------------------------------------------
// So "Today / Yesterday" separators and same-day checks agree with the times we display: the
// day an instant falls on must be computed in the SAME zone we render it in, not the host zone.

let dayKeyFmtCache = { zone: null, fmt: null };
function dayKeyFormatter() {
  const zone = appTimeZone();
  if (dayKeyFmtCache.zone !== zone) {
    dayKeyFmtCache = {
      zone,
      // en-CA happens to emit YYYY-MM-DD, but formatToParts below is locale-independent anyway.
      fmt: new Intl.DateTimeFormat("en-CA", {
        timeZone: zone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
    };
  }
  return dayKeyFmtCache.fmt;
}

/** "YYYY-MM-DD" for the given instant, in the app zone. "" for invalid input. */
export function appDayKey(dateLike) {
  const date = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(date.getTime())) return "";
  let year = "";
  let month = "";
  let day = "";
  for (const part of dayKeyFormatter().formatToParts(date)) {
    if (part.type === "year") year = part.value;
    else if (part.type === "month") month = part.value;
    else if (part.type === "day") day = part.value;
  }
  return year && month && day ? `${year}-${month}-${day}` : "";
}

/**
 * Whole calendar days between two instants (a − b), counted in the app zone. DST-safe: each key is
 * anchored at UTC midnight of its civil date, so the difference is pure calendar arithmetic.
 * NaN if either input is invalid.
 */
export function appDayDiff(aLike, bLike) {
  const a = appDayKey(aLike);
  const b = appDayKey(bLike);
  if (!a || !b) return NaN;
  return Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86400000);
}

/** True when two instants fall on the same civil day, in the app zone. */
export function appIsSameDay(aLike, bLike) {
  const a = appDayKey(aLike);
  return Boolean(a) && a === appDayKey(bLike);
}

// --- timezone catalogue for the picker -------------------------------------------------------

// Curated fallback for the rare runtime without Intl.supportedValuesOf. Covers the clinics'
// regions plus the usual global anchors; the real list (400+ zones) is used whenever available.
const FALLBACK_TIME_ZONES = Object.freeze([
  "UTC",
  "Asia/Singapore",
  "Asia/Jakarta",
  "Asia/Kuala_Lumpur",
  "Asia/Bangkok",
  "Asia/Manila",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Australia/Sydney",
  "Europe/London",
  "Europe/Warsaw",
  "Europe/Paris",
  "Europe/Berlin",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
]);

let cachedZoneList = null;
/** All selectable IANA zone names, sorted. Uses Intl.supportedValuesOf when present. */
export function listTimeZones() {
  if (cachedZoneList) return cachedZoneList;
  let zones = null;
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      const values = Intl.supportedValuesOf("timeZone");
      if (Array.isArray(values) && values.length > 0) {
        zones = values;
      }
    }
  } catch {
    /* fall through to the curated list */
  }
  cachedZoneList = Object.freeze([...(zones || FALLBACK_TIME_ZONES)].sort());
  return cachedZoneList;
}

/** True if `zone` is a usable IANA identifier on this runtime. "" is not a valid zone. */
export function isValidTimeZone(zone) {
  const value = typeof zone === "string" ? zone.trim() : "";
  if (!value) return false;
  try {
    // Throws RangeError for an unknown zone.
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

// --- lifecycle -------------------------------------------------------------------------------

// luxon reads Settings.defaultZone for DateTime.local(), zone-less parsing, and .toLocal(). Point
// it at the override, or back to "system" (luxon's reset sentinel) when there is none.
function applyLuxonDefaultZone() {
  Settings.defaultZone = overrideTimeZone || "system";
}

function refreshFromPrefs() {
  overrideTimeZone = readOverrideFromPrefs();
  dayKeyFmtCache = { zone: null, fmt: null }; // rebuild the day-key formatter for the new zone
  applyLuxonDefaultZone();
}

// Apply immediately at module load (before the first render — renderer.jsx imports this first)…
applyLuxonDefaultZone();
// …and keep it in lockstep with the store for the rest of the session.
subscribeUserPreferences(refreshFromPrefs);
